import { Link } from 'react-router-dom';
import {
  EGB_ID,
  BOARD_ROLES,
  BOARD_FUNCTIONS,
  ARCHITECTURE_PRINCIPLES,
  ADR_CAPABILITIES,
  TECHNOLOGY_DOMAINS,
  DOMAIN_GOVERNANCE,
  INTEGRATION_STANDARDS,
  DATABASE_GOVERNANCE,
  AI_GOVERNANCE,
  PERFORMANCE_GOVERNANCE,
  SECURITY_GOVERNANCE,
  UX_GOVERNANCE,
  DOCUMENTATION_GOVERNANCE,
  TECHNICAL_DEBT,
  COMPLIANCE_REVIEW,
  FUTURE_ARCHITECTURE,
  ARCHITECTURE_STAGES,
  LIVE_ROUTES,
  DEMO_BOARD,
  DEMO_PRINCIPLES,
  DEMO_ADRS,
  DEMO_TECHNOLOGY,
  DEMO_DOMAIN_INTEGRATION,
  DEMO_DATABASE_AI,
  DEMO_PERF_SEC_UX,
  DEMO_COMPLIANCE,
  buildGovernanceDashboard,
} from '../../architecturegovernance/framework';
import {
  GovernanceBoardPanel,
  ArchitecturePrinciplesPanel,
  ADRGovernancePanel,
  TechnologyGovernancePanel,
  DomainIntegrationPanel,
  DatabaseAIGovernancePanel,
  PerformanceSecurityUXPanel,
  ComplianceEvolutionPanel,
} from '../../components/architecturegovernance';
import { Card, KpiCard } from '../../components/ui';

export function ArchitectureGovernancePage() {
  const dashboard = buildGovernanceDashboard();

  return (
    <div className="space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">{EGB_ID}</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Enterprise Architecture Governance</h1>
        <p className="mt-1 text-sm text-slate-500">
          Permanent architectural governance framework — ensures consistency, technical excellence,
          maintainability, and controlled evolution. Fully aligned with MPC-00001.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(LIVE_ROUTES).map(([key, path]) => (
            <Link
              key={key}
              to={path}
              className="rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950 dark:text-violet-300"
            >
              {path}
            </Link>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Board Roles" value={dashboard.board_roles.toString()} />
        <KpiCard label="Architecture Principles" value={dashboard.architecture_principles.toString()} />
        <KpiCard label="Technology Domains" value={dashboard.technology_domains.toString()} />
        <KpiCard label="Integration Standards" value={dashboard.integration_standards.toString()} />
        <KpiCard label="Compliance Areas" value={dashboard.compliance_areas.toString()} />
      </div>

      <section>
        <h2 className="text-lg font-semibold">Governance Architecture</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {ARCHITECTURE_STAGES.map((s, i) => (
            <span key={s} className="flex items-center gap-1 text-xs">
              <span className="rounded-lg border border-slate-200 px-2 py-1 dark:border-slate-700">{s}</span>
              {i < ARCHITECTURE_STAGES.length - 1 && <span className="text-slate-400">→</span>}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Architecture Governance Board</h2>
        <p className="mt-1 text-sm text-slate-500">{BOARD_ROLES.join(' · ')}</p>
        <p className="mt-1 text-xs text-slate-400">{BOARD_FUNCTIONS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <GovernanceBoardPanel members={DEMO_BOARD} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Architecture Principles</h2>
        <p className="mt-1 text-sm text-slate-500">{ARCHITECTURE_PRINCIPLES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ArchitecturePrinciplesPanel principles={DEMO_PRINCIPLES} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Architecture Decision Records</h2>
        <p className="mt-1 text-sm text-slate-500">{ADR_CAPABILITIES.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <ADRGovernancePanel adrs={DEMO_ADRS} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Technology Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{TECHNOLOGY_DOMAINS.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <TechnologyGovernancePanel items={DEMO_TECHNOLOGY} />
        </Card>
      </section>

      <section className="grid gap-6 lg:grid-cols-2">
        <div>
          <h2 className="text-lg font-semibold">Domain Governance</h2>
          <p className="mt-1 text-sm text-slate-500">{DOMAIN_GOVERNANCE.join(' · ')}</p>
        </div>
        <div>
          <h2 className="text-lg font-semibold">Integration Governance</h2>
          <p className="mt-1 text-sm text-slate-500">{INTEGRATION_STANDARDS.join(' · ')}</p>
        </div>
      </section>
      <Card padding="sm">
        <DomainIntegrationPanel items={DEMO_DOMAIN_INTEGRATION} />
      </Card>

      <section>
        <h2 className="text-lg font-semibold">Database & AI Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{DATABASE_GOVERNANCE.join(' · ')} · {AI_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <DatabaseAIGovernancePanel items={DEMO_DATABASE_AI} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Performance, Security & UX Governance</h2>
        <p className="mt-1 text-sm text-slate-500">{PERFORMANCE_GOVERNANCE.join(' · ')} · {SECURITY_GOVERNANCE.join(' · ')} · {UX_GOVERNANCE.join(' · ')}</p>
        <Card className="mt-4" padding="sm">
          <PerformanceSecurityUXPanel items={DEMO_PERF_SEC_UX} />
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold">Compliance, Documentation & Future Architecture</h2>
        <p className="mt-1 text-sm text-slate-500">
          {DOCUMENTATION_GOVERNANCE.join(' · ')} · {TECHNICAL_DEBT.join(' · ')} · {COMPLIANCE_REVIEW.join(' · ')} · {FUTURE_ARCHITECTURE.join(' · ')}
        </p>
        <Card className="mt-4" padding="sm">
          <ComplianceEvolutionPanel items={DEMO_COMPLIANCE} />
        </Card>
      </section>
    </div>
  );
}
